import React, { useEffect, useRef, useState } from 'react'
import { BarsArrowDownIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import { useLoadboardTheme } from './loadboardTheme'
import LoadExpandedRow from './LoadExpandedRow'
import { SOURCE_LOGOS } from './sourceLogos'

const SORT_OPTIONS = [
	{ value: 'pickup', label: 'Pick Date' },
	{ value: 'lastSeen', label: 'Last seen' },
	{ value: 'pay', label: 'Rate highest' },
]

function SortDropdown({ value, onChange, tokens }) {
	const [isOpen, setIsOpen] = useState(false)
	const wrapRef = useRef(null)

	useEffect(() => {
		if (!isOpen) return
		const onClick = e => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false)
		}
		document.addEventListener('mousedown', onClick)
		return () => document.removeEventListener('mousedown', onClick)
	}, [isOpen])

	const current = SORT_OPTIONS.find(o => o.value === value) || SORT_OPTIONS[0]

	return (
		<div ref={wrapRef} className="relative">
			<button
				type="button"
				onClick={() => setIsOpen(v => !v)}
				className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${tokens.sortBtn}`}
			>
				<BarsArrowDownIcon className="h-3.5 w-3.5" strokeWidth={2} />
				{current.label}
				<ChevronDownIcon className="h-3 w-3" />
			</button>
			{isOpen && (
				<div
					className={`absolute right-0 top-full mt-1 z-30 min-w-[160px] rounded-md py-1 ${tokens.dropdownBg}`}
				>
					{SORT_OPTIONS.map(opt => (
						<button
							key={opt.value}
							type="button"
							onClick={() => {
								onChange(opt.value)
								setIsOpen(false)
							}}
							className={`w-full text-left px-3 py-2 text-sm ${tokens.dropdownItem} ${
								opt.value === value ? 'font-bold' : ''
							}`}
						>
							{opt.label}
						</button>
					))}
				</div>
			)}
		</div>
	)
}

const renderPickup = (iso, mutedCls, timeZone) => {
	if (!iso) return '—'
	const d = new Date(iso)
	if (!Number.isFinite(d.getTime())) return '—'
	// When the load's IANA zone is known, render the date/time in that zone and
	// append its short abbreviation (e.g. CDT, EST). Otherwise use local time.
	const tzOpt = timeZone ? { timeZone } : {}
	const day = d.toLocaleDateString('en-US', { weekday: 'short', ...tzOpt })
	const md = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', ...tzOpt })
	const time = d.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		...tzOpt,
	})
	let tzAbbr = ''
	if (timeZone) {
		const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
			.formatToParts(d)
			.find(p => p.type === 'timeZoneName')
		tzAbbr = part ? ` ${part.value}` : ''
	}
	return (
		<span className={`font-normal ${mutedCls}`}>
			{day} {md} {time}
			{tzAbbr}
		</span>
	)
}

const fmtElapsed = iso => {
	if (!iso) return '—'
	const ms = Date.now() - new Date(iso).getTime()
	const min = Math.max(0, Math.floor(ms / 60000))

	if (min < 2) return '1m'
	if (min < 60) return `${min}m`
	const h = Math.floor(min / 60)
	if (h < 24) return `${h}h`
	const d = Math.floor(h / 24)
	if (d < 30) return `${d}d`
	const mo = Math.floor(d / 30)
	if (mo < 12) return `${mo}mo`
	return `${Math.floor(d / 365)}yr`
}

// Freshness dot: green = synced within the hour, amber = 1–4h, gray = older.
const freshnessDotCls = iso => {
	if (!iso) return 'bg-gray-400'
	const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000))
	if (min < 60) return 'bg-emerald-500'
	if (min < 240) return 'bg-amber-400'
	return 'bg-gray-400'
}

const fmtRate = n =>
	n == null
		? '—'
		: `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const fmtMiles = n => {
	if (n == null) return '—'
	return Number(n).toLocaleString()
}

const fmtLoadDuration = (startIso, endIso) => {
	if (!startIso || !endIso) return '—'
	const start = new Date(startIso).getTime()
	const end = new Date(endIso).getTime()
	if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '—'
	const totalHours = Math.max(1, Math.round((end - start) / 3600000))
	const days = Math.floor(totalHours / 24)
	const hours = totalHours % 24
	if (days > 0) return `${days}d ${hours}h`
	return `${hours}h`
}

const fmtWeight = w => {
	if (w == null || w === '') return '—'
	const n = Number(w)
	if (!Number.isFinite(n)) return w
	return n.toLocaleString()
}

const fmtEquipment = equipment => {
	const raw = String(equipment || '').trim()
	if (!raw) return '—'
	const normalized = raw.toLowerCase().replace(/[^a-z0-9]+/g, '')
	if (normalized === 'dryvanwteam' || normalized === 'vanwteam' || normalized === 'vanteam')
		return 'Van Team'
	if (normalized === 'dryvan' || normalized === 'van' || normalized === 'driving') return 'Van'
	if (normalized === 'poweronly') return 'PO'
	return raw
}

const getBrokerContact = load => {
	const phone = String(load.brokerPhone || '').trim()
	if (phone) {
		const tel = phone.replace(/[^\d+]/g, '')
		return { label: phone, href: `tel:${tel || phone}` }
	}
	const email = String(load.brokerEmail || '').trim()
	if (email) return { label: email, href: `mailto:${email}` }
	return null
}

const EARTH_RADIUS_MI = 3958.8
const haversineMi = (lat1, lng1, lat2, lng2) => {
	if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null
	const toRad = d => (d * Math.PI) / 180
	const dLat = toRad(lat2 - lat1)
	const dLng = toRad(lng2 - lng1)
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
	return 2 * EARTH_RADIUS_MI * Math.asin(Math.sqrt(a))
}

function LocationCell({ city, state, dh, stopNumber, dateIso, timeZone, tokens, isLight = false }) {
	return (
		<div className="flex items-stretch gap-1.5 min-w-0">
			{stopNumber != null && (
				<span
					title={`Stop ${stopNumber}`}
					className={`shrink-0 self-center rounded px-1.5 text-[13px] font-bold leading-[18px] ${
						isLight ? 'bg-sky-100 text-sky-700' : 'bg-sky-500/20 text-sky-300'
					}`}
				>
					{stopNumber}
				</span>
			)}
			<div className="flex flex-col min-w-0">
				<div className="flex min-w-0 items-baseline gap-1 whitespace-nowrap">
					<span className={`min-w-0 truncate font-semibold ${tokens.valueStrong}`}>{city}</span>
					<span className={`shrink-0 font-semibold ${tokens.valueStrong} text-[12.5px]`}>
						{state}
					</span>
					{dh != null && (
						<span
							className={`shrink-0 text-[11.5px] ${tokens.valueMuted}`}
						>
							({Math.round(dh)} mi)
						</span>
					)}
				</div>
				<div className={`text-[10px] font-normal whitespace-nowrap ${tokens.valueMuted}`}>
					{renderPickup(dateIso, tokens.valueMuted, timeZone)}
				</div>
			</div>
		</div>
	)
}

function SourcePill({ name, isLight }) {
	const logo = SOURCE_LOGOS[name]
	if (logo) {
		const filter =
			!isLight && name === 'TQL'
				? 'invert(1) hue-rotate(180deg)'
				: !isLight && name === 'RXO'
					? 'invert(1)'
					: undefined
		const heightCls =
			name === 'JBHUNT'
				? 'h-4 -ml-2'
				: name === 'RXO'
					? 'h-3'
					: name === 'LANDSTAR'
						? 'h-4 -ml-5'
						: 'h-4'
		return (
			<img
				src={logo}
				alt={name}
				className={`${heightCls} w-auto max-w-full`}
				style={filter ? { filter } : undefined}
			/>
		)
	}
	return (
		<span className={`text-[12.5px] font-medium ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
			{name}
		</span>
	)
}

function LoadsTable({
	loads = [],
	hasMore = false,
	loading = false,
	errorMessage = null,
	toolbar = null,
	sort = 'pickup',
	onSortChange = () => {},
	showArchive = false,
	onShowArchiveChange = () => {},
	searchOrigin = null,
}) {
	const { theme, tokens } = useLoadboardTheme()
	const isLight = theme === 'light'
	// Per-row expansion state. Multiple rows can be open at the same time.
	const [expandedIds, setExpandedIds] = useState(() => new Set())
	const toggleExpanded = id =>
		setExpandedIds(prev => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})

	const LOADER_MIN_MS = 700
	const [showLoader, setShowLoader] = useState(false)
	const loaderStartRef = useRef(0)
	useEffect(() => {
		if (loading && loads.length > 0) {
			if (!showLoader) {
				loaderStartRef.current = Date.now()
				setShowLoader(true)
			}
			return
		}
		if (!showLoader) return
		const remaining = Math.max(0, LOADER_MIN_MS - (Date.now() - loaderStartRef.current))
		const id = setTimeout(() => setShowLoader(false), remaining)
		return () => clearTimeout(id)
	}, [loading, loads.length, showLoader])

	return (
		<div className="pt-3">
			<style>{`
				@keyframes ll-indeterminate {
					0% { transform: translateX(-100%); }
					100% { transform: translateX(400%); }
				}
			`}</style>
			<div className="flex items-center justify-between pb-2 mb-1">
				<div className="flex items-center gap-5">{toolbar}</div>
				<div className="flex items-center gap-3">
					<label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
						<span className="text-xs font-medium">Show old loads</span>
						<button
							type="button"
							role="switch"
							aria-checked={Boolean(showArchive)}
							onClick={() => onShowArchiveChange(!showArchive)}
							className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
								showArchive ? 'bg-sky-500' : isLight ? 'bg-gray-300' : 'bg-white/20'
							}`}
						>
							<span
								className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
									showArchive ? 'translate-x-3.5' : 'translate-x-0.5'
								}`}
							/>
						</button>
					</label>
					<SortDropdown value={sort} onChange={onSortChange} tokens={tokens} />
				</div>
			</div>

			<div className="overflow-x-auto relative">
				<div className="absolute top-0 left-0 right-0 h-0.5 overflow-hidden z-10">
					{showLoader && loads.length > 0 && (
						<div className="h-full w-1/3 bg-sky-500 animate-[ll-indeterminate_0.7s_linear_infinite]" />
					)}
				</div>
				<table
					className="w-full min-w-[64rem] text-[11px] table-fixed border-separate"
					style={{ borderSpacing: '0 6px' }}
				>
					<colgroup>
						<col style={{ width: '7%' }} />
						<col style={{ width: '19%' }} />
						<col style={{ width: '18%' }} />
						<col style={{ width: '8%' }} />
						<col style={{ width: '14%' }} />
						<col style={{ width: '11%' }} />
						<col style={{ width: '8%' }} />
						<col style={{ width: '15%' }} />
					</colgroup>
					<tbody
						className={
							loading && loads.length > 0 ? 'opacity-60 transition-opacity' : 'transition-opacity'
						}
					>
						{loads.map(load => {
							const isExpanded = expandedIds.has(load.id)
							const contact = getBrokerContact(load)
							const lengthStr = Array.isArray(load.length) && load.length.length > 0
								? load.length.join('/')
								: ''
							const typeStr = String(load.loadType ?? '').trim()
							const equipmentParts = [lengthStr && `${lengthStr}'`, typeStr].filter(Boolean)
							const borderCls = load.isNew
								? isLight
									? '[&>td]:border-emerald-400'
									: '[&>td]:border-emerald-500/50'
								: isLight
									? '[&>td]:border-gray-200'
									: '[&>td]:border-white/10'
							const labelCls = `text-[10px] font-normal ${tokens.valueMuted} mt-0.5`
							return (
								<React.Fragment key={load.id}>
									<tr
										onClick={() => toggleExpanded(load.id)}
										className={`cursor-pointer [&>td]:align-top [&>td]:border-y ${borderCls} [&>td:first-child]:rounded-l-lg [&>td:first-child]:border-l [&>td:last-child]:rounded-r-lg [&>td:last-child]:border-r`}
									>
										<td
											className={`pl-3 pr-1.5 py-2.5 text-left font-semibold ${tokens.valueStrong}`}
										>
											<div className="flex items-center gap-1.5">
												<span
													className={`w-2 h-2 rounded-full shrink-0 ${freshnessDotCls(load.lastSyncedAt)}`}
												/>
												<span>{fmtElapsed(load.lastSyncedAt)}</span>
											</div>
											<div className={labelCls}>Last seen</div>
										</td>
										<td className="overflow-hidden pl-4 pr-1.5 py-2.5">
											<LocationCell
												city={load.originCity}
												state={load.originState}
												dh={haversineMi(
													searchOrigin?.lat,
													searchOrigin?.lng,
													load.originLat,
													load.originLng,
												)}
												dateIso={load.pickupDate}
												timeZone={load.originTimezone}
												stopNumber={1}
												tokens={tokens}
												isLight={isLight}
											/>
										</td>
										<td className="overflow-hidden px-1.5 py-2.5">
											<LocationCell
												city={load.deliveryCity}
												state={load.deliveryState}
												dateIso={load.deliveryAppointmentStart}
												timeZone={load.deliveryTimezone}
												stopNumber={
													Array.isArray(load.stops) && load.stops.length > 0 ? load.stops.length : 2
												}
												tokens={tokens}
												isLight={isLight}
											/>
										</td>
										<td className="pl-0 pr-1.5 py-2.5 text-left whitespace-nowrap font-semibold">
											<div
												className={`leading-[14px] ${tokens.valueStrong}`}
											>
												{fmtMiles(load.distance)}
												{load.distance != null && (
													<span
														className={`ml-0.5 text-[11.5px] ${tokens.valueMuted}`}
													>
														mi
													</span>
												)}
											</div>
											<div
												className={`text-[10px] font-normal leading-[14px] ${tokens.valueMuted}`}
											>
												{fmtLoadDuration(
													load.pickupAppointmentStart || load.pickupDate,
													load.deliveryAppointmentEnd || load.deliveryAppointmentStart,
												)}
											</div>
										</td>
										<td
											className={`overflow-hidden pl-8 pr-1.5 py-2.5 text-left whitespace-nowrap font-semibold text-[12px] ${tokens.valueDim}`}
										>
											<div className="flex min-w-0 items-center justify-start gap-1 leading-[14px]">
												<span title={load.equipment || undefined} className="min-w-0 truncate">
													{fmtEquipment(load.equipment)}
												</span>
												{equipmentParts.length > 0 && (
													<span className={`shrink-0 ${tokens.valueMuted}`}>
														{equipmentParts.map((part, i) => (
															<React.Fragment key={i}>
																{i > 0 && (
																	<span className={`mx-1 font-bold ${tokens.valueStrong}`}>•</span>
																)}
																{part}
															</React.Fragment>
														))}
													</span>
												)}
												{load.hazmat === true && (
													<span
														title="Hazmat"
														className={`shrink-0 rounded px-1 text-[10.5px] font-bold leading-[14px] ${
															isLight ? 'bg-red-100 text-red-700' : 'bg-red-500/20 text-red-300'
														}`}
													>
														HAZ
													</span>
												)}
											</div>
											<div
												className={`leading-[14px] text-[10px] font-normal ${tokens.valueMuted}`}
											>
												{fmtWeight(load.weight)}
												{load.weight != null && load.weight !== '' && (
													<span className="ml-0.5">lbs</span>
												)}
											</div>
										</td>
										<td className="pl-0 pr-1.5 py-2.5 text-left">
											<div className="inline-block text-left">
												<div
													className={`font-semibold ${tokens.rateMain}`}
												>
													{fmtRate(load.price)}
												</div>
												<div
													className={`text-[10px] font-normal leading-4 min-h-[1rem] ${tokens.valueMuted}`}
												>
													{Number(load.pricePerMile) > 0
														? `$${Number(load.pricePerMile).toFixed(2)}/mi`
														: ''}
												</div>
											</div>
										</td>
										<td className={`pl-1.5 pr-1.5 py-2.5 text-center ${tokens.valueDim}`}>
											<div className="inline-block max-w-full text-left">
												{contact ? (
													<a
														href={contact.href}
														target="_blank"
														rel="noopener noreferrer"
														onClick={e => e.stopPropagation()}
														title={contact.label}
														className={`block max-w-full truncate text-[11px] font-medium leading-[14px] ${
															isLight
																? 'text-sky-600 hover:text-sky-700'
																: 'text-sky-400 hover:text-sky-300'
														}`}
													>
														{contact.label}
													</a>
												) : (
													<span className={tokens.valueMuted}>—</span>
												)}
												{load.brokerName && (
													<div
														title={load.brokerName}
														className={`max-w-full truncate text-[10px] font-normal leading-[13px] ${tokens.valueMuted}`}
													>
														{load.brokerName}
													</div>
												)}
											</div>
										</td>
										<td className="pl-1.5 pr-3 py-2.5">
											<div className="flex items-center justify-end min-h-[18px]">
												<SourcePill name={load.source} isLight={isLight} />
											</div>
											<div className={`${labelCls} text-right`}>Source</div>
										</td>
									</tr>
									{isExpanded && (
										<tr>
											<LoadExpandedRow load={load} tokens={tokens} isLight={isLight} />
										</tr>
									)}
								</React.Fragment>
							)
						})}
					</tbody>
				</table>

				{loads.length === 0 && errorMessage && !loading ? (
					<div className="flex justify-center pt-24 pb-12">
						<div
							role="alert"
							className={`inline-flex items-start gap-2 px-3 py-2 rounded-md text-sm font-medium ring-1 border-l-4 ${
								isLight
									? 'bg-amber-50 text-amber-900 ring-amber-200 border-amber-500'
									: 'bg-amber-500/15 text-amber-200 ring-amber-400/30 border-amber-400'
							}`}
						>
							<ExclamationTriangleIcon
								className={`h-5 w-5 shrink-0 mt-px ${isLight ? 'text-amber-600' : 'text-amber-400'}`}
							/>
							<span>{errorMessage}</span>
						</div>
					</div>
				) : (
					loads.length === 0 &&
					(loading ? (
						<div
							className="flex justify-center pt-40 pb-12"
							role="status"
							aria-label="Loading loads"
						>
							<div
								className={`h-12 w-12 rounded-full border-[5px] border-t-transparent animate-spin ${
									isLight ? 'border-sky-500' : 'border-sky-400'
								}`}
							/>
						</div>
					) : (
						<div className={`text-center pt-24 pb-12 text-xl font-semibold ${tokens.valueStrong}`}>
							No loads match this search.
						</div>
					))
				)}
			</div>
		</div>
	)
}

export default LoadsTable
